import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Typography, Spin, Alert, List, Avatar, Tag } from 'antd';
import { AreaChartOutlined, UserOutlined, SolutionOutlined, ProjectOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
// Import mock data (or functions to fetch it)
import { getStats } from '../../services/otherService'; 
import { getQuotes } from '../../services/quoteService'; 


const { Title, Text } = Typography;

const DashboardCard = styled(Card)`
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
  border-radius: 8px;
  .ant-card-body {
    padding: 20px 24px;
  }
`;

// Helper to get status tag rendering logic (copied/imported from QuoteList)
const statusOptions = [
    { value: 'Pending', label: 'Pending', color: 'orange', icon: <ClockCircleOutlined /> },
    { value: 'Confirmed', label: 'Confirmed', color: 'green', icon: <CheckCircleOutlined /> },
    { value: 'Processing', label: 'Processing', color: 'blue', icon: <ClockCircleOutlined /> },
    { value: 'Completed', label: 'Completed', color: 'purple', icon: <CheckCircleOutlined /> },
    { value: 'Rejected', label: 'Rejected', color: 'red', icon: <CloseCircleOutlined /> },
];
const getStatusTag = (status) => {
    const statusConfig = statusOptions.find(s => s.value === status) || { color: 'default', label: status, icon: null };
    return <Tag icon={statusConfig.icon} color={statusConfig.color}>{statusConfig.label}</Tag>;
};


const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError(null);
      try {
        const recentQuotesParams = {
          sortBy: 'createdAt',
          sortOrder: 'desc',
          limit: 5
          // Or adapt to: { current: 1, pageSize: 5, sortField: 'createdAt', sortOrder: 'descend' }
          // Or adapt to: { page: 1, pageSize: 5 }
        };

        const [statsData, quotesApiResponse] = await Promise.all([
          getStats(),
          getQuotes(recentQuotesParams)
        ]);

        setStats(statsData);

        // Adjust based on your getQuotes response structure
        const quotesArray = quotesApiResponse?.data || quotesApiResponse?.quotes || quotesApiResponse || [];
        setRecentQuotes(quotesArray);

      } catch (err) {
        console.error("Failed to load dashboard data:", err);
        setError(err.message || "Could not load dashboard information.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>;
  }

  if (error) {
    return <Alert message="Error" description={error} type="error" showIcon />;
  }

  return (
    <div>
      <Title level={2} style={{ marginBottom: '24px' }}>Welcome back, {user?.name || 'Admin'}!</Title>

      {/* Statistics Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={12} lg={6}>
          <DashboardCard>
            <Statistic
              title="Total Quotes"
              value={stats?.totalQuotes}
              valueStyle={{ color: '#1890ff' }}
              prefix={<SolutionOutlined />}
             // valueRender={value => <StatisticValue>{value}</StatisticValue>} // Custom render
            />
          </DashboardCard>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6}>
          <DashboardCard>
            <Statistic
              title="Pending Quotes"
              value={stats?.pendingQuotes}
              valueStyle={{ color: '#faad14' }}
              prefix={<ClockCircleOutlined />}
            />
          </DashboardCard>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6}>
          <DashboardCard>
            <Statistic
              title="Total Users"
              value={stats?.totalUsers}
               valueStyle={{ color: '#52c41a' }}
              prefix={<UserOutlined />}
            />
          </DashboardCard>
        </Col>
        <Col xs={24} sm={12} md={12} lg={6}>
          <DashboardCard>
            <Statistic
              title="Projects In Progress"
              value={stats?.projectsInProgress}
               valueStyle={{ color: '#722ed1' }}
              prefix={<ProjectOutlined />}
            />
          </DashboardCard>
        </Col>
      </Row>

      {/* Recent Activity / Charts Row */}
       <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
                <DashboardCard title="Recent Quotes">
                     <List
                        itemLayout="horizontal"
                        dataSource={recentQuotes}
                        renderItem={item => (
                        <List.Item
                             actions={[getStatusTag(item.status), <Text type="secondary">{new Date(item.createdAt).toLocaleDateString()}</Text>]}
                        >
                            <List.Item.Meta
                            avatar={<Avatar style={{ backgroundColor: '#1890ff' }}>{item.name.charAt(0)}</Avatar>}
                            title={<a href={`/quotes?search=${item.id}`}>{item.name}</a>} // Link to quote page maybe
                            description={item.projectType}
                            />
                        </List.Item>
                        )}
                        locale={{ emptyText: 'No recent quotes found.' }}
                    />
                </DashboardCard>
            </Col>
            <Col xs={24} lg={12}>
                <DashboardCard title="Performance Overview">
                    {/* Placeholder for charts */}
                    <div style={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa' }}>
                         <AreaChartOutlined style={{ fontSize: '48px', marginRight: '10px' }} /> Chart Placeholder
                    </div>
                </DashboardCard>
            </Col>
       </Row>

    </div>
  );
};

export default DashboardPage;